import React, { useState } from 'react';
import { FaTicketAlt, FaEnvelope, FaLock, FaCheckCircle, FaArrowLeft, FaPaperclip, FaSpinner } from 'react-icons/fa';
import { requestSupportOtpAPI, verifyOtpAndSubmitTicketAPI, generateSecureUploadUrlAPI } from '../services/serverService'; 

const CATEGORIES = ['Bug/Crash', 'Feature Request', 'Billing/Invoice', 'Account/Login Issue', 'Other'];

export default function PublicSupportPortal() {
    const [step, setStep] = useState(1); // 1: Form, 2: OTP, 3: Success
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        username: '',
        email: '',
        subject: '',
        category: CATEGORIES[0],
        priority: 'Medium',
        description: '',
        attachments: []
    });
    
    const [otp, setOtp] = useState('');
    const [createdTicketId, setCreatedTicketId] = useState('');

    // --- Secure File Upload Handler ---
    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setIsUploading(true);
        setError('');

        try {
            const newAttachments = [];
            for (const file of files) {
                // Strict Size Checks (Matches Backend Limits)
                const isVideo = file.type.startsWith('video/');
                const maxSize = isVideo ? 20 * 1024 * 1024 : 5 * 1024 * 1024; // 20MB or 5MB
                
                if (file.size > maxSize) {
                    throw new Error(`File ${file.name} exceeds the ${isVideo ? '20MB' : '5MB'} limit.`);
                }

                // 1. Get Secure Signed URL from Backend
                const res = await generateSecureUploadUrlAPI({
                    fileName: file.name,
                    fileType: file.type,
                    fileSize: file.size
                });
                
                const { uploadUrl, fileUrl, mimeType, fileName } = res.data.data;

                // 2. Upload directly to Google Cloud Storage via the Signed URL
                const uploadResponse = await fetch(uploadUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': mimeType },
                    body: file
                });

                if (!uploadResponse.ok) throw new Error('Failed to upload file to server.');

                // 3. Save the permanent URL to our form state
                newAttachments.push({ fileName, fileUrl, mimeType, uploadedAt: new Date().toISOString() });
            }
            
            setFormData(prev => ({
                ...prev,
                attachments: [...prev.attachments, ...newAttachments]
            }));
        } catch (err) {
            setError(err.message || 'File upload failed. Only images, PDFs, and small videos are allowed.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleRequestOTP = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            await requestSupportOtpAPI(formData.email, formData.username, true);
            setStep(2);
        } catch (err) {
            // Display the exact backend error message if the account doesn't exist
            setError(err.response?.data?.message || 'Failed to send verification code. Check credentials.');
        } finally { setIsLoading(false); }
    };

    const handleVerifyAndSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const res = await verifyOtpAndSubmitTicketAPI(formData.email, otp, formData);
            setCreatedTicketId(res.data.data?.id || res.data.data?.ticketId || 'Success');
            setStep(3);
        } catch (err) {
            setError('Invalid verification code or submission failed.');
        } finally {
            setIsLoading(false);
        }
    };

    if (step === 3) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#f4f7fa', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '500px', width: '100%', textAlign: 'center' }}>
                    <FaCheckCircle style={{ color: '#38a169', fontSize: '64px', marginBottom: '20px' }} />
                    <h2 style={{ color: '#2d3748', marginBottom: '10px' }}>Ticket Submitted!</h2>
                    <p style={{ color: '#718096', marginBottom: '20px' }}>Your support request has been securely verified and submitted. Our team will review it shortly.</p>
                    <div style={{ backgroundColor: '#edf2f7', padding: '15px', borderRadius: '6px', fontSize: '18px', fontWeight: 'bold', color: '#2b6cb0', marginBottom: '10px', letterSpacing: '1px' }}>
                        {createdTicketId}
                    </div>
                    <p style={{ fontSize: '14px', color: '#e53e3e', fontWeight: 'bold', marginBottom: '30px' }}>Please save this Ticket ID. We have also emailed it to you.</p>
                    <button onClick={() => window.location.reload()} style={{ padding: '10px 24px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
                        Submit Another Request
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f4f7fa', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
            <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '600px', width: '100%' }}>
                
                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', color: '#0056b3', marginBottom: '10px' }}>
                        <FaTicketAlt style={{ fontSize: '28px' }} />
                        <h1 style={{ margin: 0, fontSize: '24px' }}>Health Automated Support</h1>
                    </div>
                    <p style={{ color: '#718096', margin: 0 }}>Secure Helpdesk Portal</p>
                </div>

                {error && <div style={{ backgroundColor: '#fff5f5', color: '#c53030', padding: '12px', borderRadius: '6px', marginBottom: '20px', border: '1px solid #feb2b2', fontSize: '14px' }}>{error}</div>}

                {step === 1 && (
                    <form onSubmit={handleRequestOTP} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div style={{ display: 'flex', gap: '15px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#4a5568', marginBottom: '5px' }}>USERNAME</label>
                                <input required type="text" value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})} style={{ width: '100%', boxSizing: 'border-box', padding: '10px', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#4a5568', marginBottom: '5px' }}>WORK EMAIL</label>
                                <input required type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} style={{ width: '100%', boxSizing: 'border-box', padding: '10px', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '15px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#4a5568', marginBottom: '5px' }}>CATEGORY</label>
                                <select value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} style={{ width: '100%', boxSizing: 'border-box', padding: '10px', borderRadius: '4px', border: '1px solid #cbd5e0', backgroundColor: 'white' }}>
                                    {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                </select>
                            </div>
                            <div style={{ flex: 2 }}>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#4a5568', marginBottom: '5px' }}>SUBJECT</label>
                                <input required type="text" placeholder="Brief summary of the issue" value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} style={{ width: '100%', boxSizing: 'border-box', padding: '10px', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                            </div>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#4a5568', marginBottom: '5px' }}>DESCRIPTION</label>
                            <textarea required placeholder="Please provide as much detail as possible..." value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} style={{ width: '100%', boxSizing: 'border-box', padding: '10px', borderRadius: '4px', border: '1px solid #cbd5e0', minHeight: '120px', resize: 'vertical' }} />
                        </div>

                        {/* FILE UPLOAD SECTION */}
                        <div style={{ backgroundColor: '#f7fafc', border: '1px dashed #cbd5e0', borderRadius: '6px', padding: '15px', position: 'relative' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', color: '#4a5568', fontWeight: 'bold', fontSize: '14px' }}>
                                <FaPaperclip /> Attach Files (Images/PDFs &lt; 5MB, Videos &lt; 20MB)
                                <input type="file" multiple accept="image/*,application/pdf,video/*" onChange={handleFileUpload} style={{ display: 'none' }} disabled={isUploading} />
                            </label>
                            
                            {isUploading && <div style={{ marginTop: '10px', color: '#3182ce', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '5px' }}><FaSpinner className="fa-spin" /> Uploading secure file...</div>}
                            
                            {formData.attachments.length > 0 && (
                                <ul style={{ marginTop: '10px', paddingLeft: '20px', margin: '10px 0 0 0', fontSize: '12px', color: '#2b6cb0' }}>
                                    {formData.attachments.map((file, idx) => (
                                        <li key={idx}><strong>{file.fileName}</strong> attached successfully.</li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <button type="submit" disabled={isLoading || isUploading} style={{ marginTop: '10px', width: '100%', padding: '14px', backgroundColor: '#0056b3', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', fontSize: '16px', cursor: (isLoading || isUploading) ? 'not-allowed' : 'pointer', opacity: (isLoading || isUploading) ? 0.7 : 1 }}>
                            <FaEnvelope style={{ marginRight: '8px', position: 'relative', top: '2px' }} />
                            {isLoading ? 'Processing...' : 'Proceed to Verification'}
                        </button>
                    </form>
                )}

                {step === 2 && (
                    <form onSubmit={handleVerifyAndSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
                        <FaLock style={{ fontSize: '48px', color: '#ecc94b' }} />
                        <div style={{ textAlign: 'center' }}>
                            <h3 style={{ margin: '0 0 10px 0', color: '#2d3748' }}>Verify Your Identity</h3>
                            <p style={{ margin: 0, color: '#718096', fontSize: '14px' }}>We sent a 6-digit secure code to <strong>{formData.email}</strong>.<br/>Please enter it below to submit your ticket.</p>
                        </div>

                        <input 
                            required 
                            type="text" 
                            maxLength="6" 
                            placeholder="000000" 
                            value={otp} 
                            onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} 
                            style={{ width: '150px', textAlign: 'center', fontSize: '24px', padding: '12px', letterSpacing: '4px', borderRadius: '6px', border: '2px solid #3182ce', fontWeight: 'bold', color: '#2b6cb0' }} 
                        />

                        <div style={{ display: 'flex', gap: '15px', width: '100%' }}>
                            <button type="button" onClick={() => setStep(1)} style={{ flex: 1, padding: '12px', backgroundColor: '#edf2f7', color: '#4a5568', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>
                                <FaArrowLeft style={{ marginRight: '5px', position: 'relative', top: '2px' }} /> Back
                            </button>
                            <button type="submit" disabled={isLoading || otp.length !== 6} style={{ flex: 2, padding: '12px', backgroundColor: '#38a169', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: (isLoading || otp.length !== 6) ? 'not-allowed' : 'pointer', opacity: (isLoading || otp.length !== 6) ? 0.6 : 1 }}>
                                {isLoading ? 'Verifying...' : 'Verify & Submit Ticket'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}